/**
 * Messaging Invariants
 *
 * Permission matrix mirrored from the Invoicing subdomain, adapted to the
 * messaging command taxonomy:
 *   - `user` may open/close the add modal, select a connector, submit and delete.
 *   - `api`/`url` may set the registered-integrations list (and api may drive the
 *     submit/delete success/error lifecycle + close the modal).
 *   - `ai` is read-only: it cannot submit, delete, or set list data.
 *
 * The privileged provision trigger is `submit/start`, gated by `commandOperations`
 * via the named client method, exactly as Invoicing gates `submit/start`.
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { MessagingState } from './state';
import { MessagingCommand } from './commands';

export const commandOperations: CommandOperationMap<MessagingState, MessagingCommand> = {
  'integrations/messaging/submit/start': 'ProvisionSystemIntegration',
  // No deprovision command exists yet; gate deletion under the same
  // integration-write capability so it is not silently allowed unguarded.
  'integrations/messaging/delete/start': 'ProvisionSystemIntegration'
};

export const invariants: Invariants<MessagingState, MessagingCommand> = {
  canApply: ({ state, command, meta }) => {
    const { type } = command;
    const { source } = meta;

    // API source — drives list/list lifecycle from policies.
    if ( source === 'api' ) {
      return (
        type === 'integrations/messaging/list/set' ||
        type === 'integrations/messaging/submit/success' ||
        type === 'integrations/messaging/submit/error' ||
        type === 'integrations/messaging/delete/success' ||
        type === 'integrations/messaging/delete/error' ||
        type === 'integrations/messaging/addModal/close'
      );
    }

    // AI source — read-only: no submit, delete, or list mutation.
    if ( source === 'ai' ) {
      if (
        type === 'integrations/messaging/submit/start' ||
        type === 'integrations/messaging/delete/start' ||
        type === 'integrations/messaging/list/set'
      ) {
        return false;
      }
      return true;
    }

    // URL source — may set the registered-integrations list.
    if ( source === 'url' ) {
      return type === 'integrations/messaging/list/set';
    }

    // User source — privileged provision trigger gated by commandOperations.
    if ( source === 'user' ) {
      if ( type === 'integrations/messaging/list/set' ) return false;
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }

    return false;
  }
};
